import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Chip,
  Grid2,
  Stack,
  Typography,
  useTheme,
  CircularProgress,
  Collapse,
  Tabs,
  Tab,
} from "@mui/material";
import { EGInput } from "../../../../../MainComponents/Input/EGInput";
import { EGBasicSelect } from "../../../../../MainComponents/Select/EGBasicSelect";
import { InvestigationButton } from "../../components/InvestigationButton";
import { DrawerContainer } from "../../components/DrawerContainer";
import { InvestigationItemContainer } from "../../components/InvestigationItemContainer";
import { InputCatalogue } from "../../../../../MainComponents/Catalogues/InputCatalogue";
import { DEPARTMENT_CATALOGUE } from "../AddInvolved";
import {
  catalogByPart,
  searchExternalEmployees,
  searchInternalEmployees,
  getEmployeeDetail,
} from "../../../../../../apis";

// The buscador filters by the bare FODepartment code: the catalogue path
// "type::X71000004::type::X71000564" is sent as "71000564".
const departmentCode = (path) => {
  const key = (path || "").split("::").pop();
  const match = /^X?(\d+)$/.exec(key);
  return match ? match[1] : "";
};

export const SearchInvolved = ({
  open,
  onClose,
  onSelect,
  initialData,
}) => {
  const [t] = useTranslation("investigation");
  const theme = useTheme();
  const lang = localStorage.getItem("lang") || "es";

  // Tab selection: internal or external
  const [searchType, setSearchType] = useState("internal");

  // Internal search state (multiple filters)
  const [internalFilters, setInternalFilters] = useState({
    firstName: "",
    lastName: "",
    department: "",
    position: "",
    country: "",
    location: "",
  });

  // External search state (multiple filters)
  const [externalFilters, setExternalFilters] = useState({
    country: "",
    firstName: "",
    lastName: "",
    email: "",
    businessName: "",
  });

  // Optional filters enabled state - Internal
  const [internalOptionalFilters, setInternalOptionalFilters] = useState({
    department: false,
    position: false,
    country: false,
    location: false,
  });

  // Optional filters enabled state - External
  const [externalOptionalFilters, setExternalOptionalFilters] = useState({
    email: false,
    businessName: false,
  });

  // Catalog options
  const [positionOptions, setPositionOptions] = useState([]);

  // Common state
  const [isLoading, setIsLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [results, setResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);

  // Country options with multi-language support
  const countryOptions = [
    { value: "AR", label: t("involved.countries.argentina") },
    { value: "BR", label: t("involved.countries.brazil") },
    { value: "CL", label: t("involved.countries.chile") },
    { value: "CN", label: t("involved.countries.china") },
    { value: "CO", label: t("involved.countries.colombia") },
    { value: "EC", label: t("involved.countries.ecuador") },
    { value: "MX", label: t("involved.countries.mexico") },
    { value: "PE", label: t("involved.countries.peru") },
    { value: "US", label: t("involved.countries.unitedStates") },
    { value: "UY", label: t("involved.countries.uruguay") },
    { value: "VE", label: t("involved.countries.venezuela") },
  ];

  // Country code mapping for internal employees (2 chars -> 3 chars)
  const countryCodeMap = {
    AR: "ARG",
    BR: "BRA",
    CL: "CHL",
    CN: "CHN",
    CO: "COL",
    EC: "ECU",
    MX: "MEX",
    PE: "PER",
    US: "USA",
    UY: "URY",
    VE: "VEN",
  };

  // Load position (workstation) catalog
  useEffect(() => {
    const loadPositionOptions = async () => {
      try {
        const response = await catalogByPart({
          is_own: true,
          catalogue: "workstation",
          path: "/",
        });
        if (response?.data && Array.isArray(response.data)) {
          const options = response.data
            .filter(item => item.is_active)
            .map(item => ({
              label: item.locales?.[lang] || item.label,
              value: item.key,
            }));
          setPositionOptions(options);
        }
      } catch (error) {
        console.error("Error loading position options:", error);
      }
    };
    loadPositionOptions();
  }, [lang]);

  // Pre-fill filters when modal opens with initialData
  useEffect(() => {
    if (open && initialData) {
      // Extract firstName and lastName from initialData
      let firstName = initialData.firstName || initialData.first_name || "";
      let lastName = initialData.lastName || initialData.last_name || "";

      // If firstName/lastName not available, try to split the name
      if (!firstName && !lastName && initialData.name) {
        const nameParts = initialData.name.trim().split(" ");
        if (nameParts.length >= 2) {
          firstName = nameParts[0];
          lastName = nameParts.slice(1).join(" ");
        } else {
          firstName = initialData.name;
        }
      }

      // Pre-fill both internal and external filters
      setInternalFilters(prev => ({
        ...prev,
        firstName,
        lastName,
      }));
      setExternalFilters(prev => ({
        ...prev,
        firstName,
        lastName,
      }));
    }
  }, [open, initialData]);

  const handleInternalFilterChange = (field, value) => {
    setInternalFilters(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleExternalFilterChange = (field, value) => {
    setExternalFilters(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const toggleInternalOptionalFilter = (field) => {
    setInternalOptionalFilters(prev => ({
      ...prev,
      [field]: !prev[field],
    }));
    // Clear the filter value when disabling
    if (internalOptionalFilters[field]) {
      setInternalFilters(prev => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const toggleExternalOptionalFilter = (field) => {
    setExternalOptionalFilters(prev => ({
      ...prev,
      [field]: !prev[field],
    }));
    // Clear the filter value when disabling
    if (externalOptionalFilters[field]) {
      setExternalFilters(prev => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const canSearchInternal = () => {
    // At least firstName or lastName should have a value
    return internalFilters.firstName.trim() !== "" || internalFilters.lastName.trim() !== "";
  };

  const canSearchExternal = () => {
    // Country is required AND at least firstName or lastName should have a value
    const hasCountry = externalFilters.country !== "";
    const hasName = externalFilters.firstName.trim() !== "" || externalFilters.lastName.trim() !== "";
    return hasCountry && hasName;
  };

  const handleSearch = async () => {
    if (searchType === "internal" && !canSearchInternal()) return;
    if (searchType === "external" && !canSearchExternal()) return;

    setIsLoading(true);
    setHasSearched(true);

    try {
      let response;

      if (searchType === "internal") {
        response = await searchInternalEmployees({
          firstName: internalFilters.firstName,
          lastName: internalFilters.lastName,
          department: departmentCode(internalFilters.department),
          puesto: internalFilters.position,
          countryOfCompany: internalFilters.country ? countryCodeMap[internalFilters.country] : "",
          location: internalFilters.location,
        });
      } else {
        response = await searchExternalEmployees({
          firstName: externalFilters.firstName,
          lastName: externalFilters.lastName,
          country: externalFilters.country,
          email: externalFilters.email,
          businessName: externalFilters.businessName,
        });
      }

      // Map response data to results format
      // API function already returns resp.data, so:
      // Internal API returns: { status, message, data: { results: [...] } }
      // External API returns: { results: [...] } or { data: [...] }
      const data = searchType === "internal"
        ? response?.data?.results || response?.results || []
        : response?.data?.results || response?.results || response?.data || [];

      let mappedResults = [];

      if (searchType === "internal") {
        // Internal employees format from MercadoLibre API
        mappedResults = Array.isArray(data) ? data.map((item, index) => ({
          id: item.userId || index,
          userId: item.userId,
          name: `${item.firstName || ""} ${item.lastName || ""}`.trim(),
          firstName: item.firstName || "",
          lastName: item.lastName || "",
          email: item.email || "",
          country: item.country || "",
          department: item.department || "",
          businessUnit: item.businessUnit || "",
          location: item.jobInfo?.location || "",
          status: item.status?.toLowerCase() || "active",
          ...item,
        })) : [];
      } else {
        // External employees format
        mappedResults = Array.isArray(data) ? data.map((item, index) => ({
          id: item.ID || item.id || index,
          name: item.name || `${item.firstName || item.Nombre || ""} ${item.lastName || item.Apellido || ""}`.trim(),
          firstName: item.firstName || item.Nombre || "",
          lastName: item.lastName || item.Apellido || "",
          email: item.email || item.Email || "",
          employee_number: item.employee_number || item.employeeNumber || item.Identificador || "",
          area: item.area || item.Area || "",
          position: item.position || item.Puesto || "",
          country: item.country || item.Pais || "",
          businessName: item.companyName || item.RazonSocial || "",
          nationalId: item.nationalId || item.Identificador || "",
          companyId: item.companyId || item.IdentificadorEmpresa || "",
          status: item.status || item.Estado || "",
          ...item,
        })) : [];
      }

      setResults(mappedResults);
    } catch (error) {
      console.error("Error searching:", error);
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectResult = async (result) => {
    if (onSelect) {
      setIsImporting(true);
      try {
        let employeeData = result;

        // For internal employees, fetch full details using userId and map to form fields
        if (searchType === "internal" && result.userId) {
          const detailResponse = await getEmployeeDetail({
            userId: result.userId,
            employeeType: "internal",
          });

          const detail = detailResponse?.data || detailResponse;

          // Map internal employee detail to form fields - storing ALL information
          employeeData = {
            // Basic info
            userId: detail.userId || "",
            username: detail.username || "",
            firstName: detail.firstName || "",
            lastName: detail.lastName || "",
            name: `${detail.firstName || ""} ${detail.lastName || ""}`.trim(),
            email: detail.email || "",
            status: detail.status || "",

            // Organization
            division: detail.division || "",
            department: detail.department || "",
            businessUnit: detail.businessUnit || "",
            country: detail.country || "",

            // Dates
            hireDate: detail.hireDate || "",
            meli_entry_date: detail.hireDate || "",

            // Manager info
            manager: detail.manager || null,

            // Employee extended info (empInfo)
            empInfo: detail.empInfo || null,

            // All national IDs
            nationalIds: detail.empInfo?.personNav?.nationalIdNav || [],
            primaryNationalId: detail.primaryNationalId || null,
            rfc_tax_id: detail.primaryNationalId?.nationalId || "",

            // Job relationships
            empJobRelationships: detail.empInfo?.empJobRelationshipNav || [],

            // Job info from empInfo
            jobInfoNav: detail.empInfo?.jobInfoNav || [],

            // Processed job info
            jobInfo: detail.jobInfo || null,
            jobCode: detail.jobInfo?.jobCode || "",
            location: detail.jobInfo?.location || "",
            locationName: detail.jobInfo?.locationName || "",
            province: detail.jobInfo?.province || "",
            positionEntryDate: detail.jobInfo?.positionEntryDate || "",
            costCenter: detail.jobInfo?.costCenter || "",
            position: detail.jobInfo?.position || "",
            company: detail.jobInfo?.company || "",

            // Relationships (pbp, pbpLeader, taOwner)
            relationships: detail.relationships || null,
            pbp: detail.relationships?.pbp || null,
            pbpLeader: detail.relationships?.pbpLeader || null,
            taOwner: detail.relationships?.taOwner || null,

            // PBP Leader full info (from root level)
            pbpLeaderInfo: detailResponse?.pbpLeader || null,

            // Flags
            is_manager: detailResponse?.is_manager || false,
            employeeType: detailResponse?.employeeType || "internal",

            // Legacy field mappings for compatibility
            employee_number: detail.userId || "",
            area: detail.division || detail.department || "",
            vertical: detail.division || "",
            cargo: detail.jobInfo?.position || "",

            // Keep original response for reference
            _originalResponse: detailResponse,
          };
        }

        await onSelect(employeeData, searchType);
        handleClose();
      } catch (error) {
        console.error("Error importing user:", error);
        setIsImporting(false);
      }
    }
  };

  const resetFilters = () => {
    setInternalFilters({
      firstName: "",
      lastName: "",
      department: "",
      position: "",
      country: "",
      location: "",
    });
    setExternalFilters({
      country: "",
      firstName: "",
      lastName: "",
      email: "",
      businessName: "",
    });
    setInternalOptionalFilters({
      department: false,
      position: false,
      country: false,
      location: false,
    });
    setExternalOptionalFilters({
      email: false,
      businessName: false,
    });
  };

  const handleClose = () => {
    resetFilters();
    setResults([]);
    setHasSearched(false);
    setSearchType("internal");
    setIsImporting(false);
    onClose();
  };

  const handleTypeChange = (type) => {
    // Preserve firstName and lastName when switching tabs
    const currentFirstName = searchType === "internal"
      ? internalFilters.firstName
      : externalFilters.firstName;
    const currentLastName = searchType === "internal"
      ? internalFilters.lastName
      : externalFilters.lastName;

    setSearchType(type);
    setResults([]);
    setHasSearched(false);

    // Reset optional filters but keep name fields
    setInternalOptionalFilters({
      department: false,
      position: false,
      country: false,
      location: false,
    });
    setExternalOptionalFilters({
      email: false,
      businessName: false,
    });

    // Apply preserved name values to both filters
    setInternalFilters({
      firstName: currentFirstName,
      lastName: currentLastName,
      department: "",
      position: "",
      country: "",
      location: "",
    });
    setExternalFilters({
      firstName: currentFirstName,
      lastName: currentLastName,
      country: "",
      email: "",
      businessName: "",
    });
  };

  return (
    <DrawerContainer
      open={open}
      onClose={handleClose}
      borderColor={theme.palette.primary.main}
      maxWidth="550px"
    >
      <Box padding="10px 20px 20px 20px">
        <Typography
          fontSize={24}
          fontWeight={600}
          color="primary"
          marginBottom={2}
        >
          {t("involved.search_person")}
        </Typography>

        {/* Type selector tabs */}
        <Tabs
          value={searchType}
          onChange={(_, newValue) => handleTypeChange(newValue)}
          variant="fullWidth"
          sx={{
            marginBottom: 2,
            borderBottom: 1,
            borderColor: "divider",
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              fontSize: 14,
            },
            "& .Mui-selected": {
              color: theme.palette.primary.main,
            },
            "& .MuiTabs-indicator": {
              backgroundColor: theme.palette.primary.main,
              height: 3,
            },
          }}
        >
          <Tab label={t("involved.internal")} value="internal" />
          <Tab label={t("involved.external")} value="external" />
        </Tabs>

        {/* Internal search - multiple filters */}
        {searchType === "internal" && (
          <Stack spacing={2} marginBottom={2}>
            {/* Required fields: firstName and lastName */}
            <Grid2 container spacing={2}>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <EGInput
                  label={t("involved.first_name")}
                  value={internalFilters.firstName}
                  onChange={(e) => handleInternalFilterChange("firstName", e.target.value)}
                  placeholder={t("involved.enter_first_name")}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <EGInput
                  label={t("involved.last_name")}
                  value={internalFilters.lastName}
                  onChange={(e) => handleInternalFilterChange("lastName", e.target.value)}
                  placeholder={t("involved.enter_last_name")}
                />
              </Grid2>
            </Grid2>

            {/* Optional filters chips */}
            <Box>
              <Typography fontSize={12} color="secondary" mb={1}>
                {t("involved.optional_filters")}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  label={t("involved.department")}
                  onClick={() => toggleInternalOptionalFilter("department")}
                  color={internalOptionalFilters.department ? "primary" : "default"}
                  variant={internalOptionalFilters.department ? "filled" : "outlined"}
                  size="small"
                />
                <Chip
                  label={t("involved.position")}
                  onClick={() => toggleInternalOptionalFilter("position")}
                  color={internalOptionalFilters.position ? "primary" : "default"}
                  variant={internalOptionalFilters.position ? "filled" : "outlined"}
                  size="small"
                />
                <Chip
                  label={t("involved.country")}
                  onClick={() => toggleInternalOptionalFilter("country")}
                  color={internalOptionalFilters.country ? "primary" : "default"}
                  variant={internalOptionalFilters.country ? "filled" : "outlined"}
                  size="small"
                />
                <Chip
                  label={t("involved.location")}
                  onClick={() => toggleInternalOptionalFilter("location")}
                  color={internalOptionalFilters.location ? "primary" : "default"}
                  variant={internalOptionalFilters.location ? "filled" : "outlined"}
                  size="small"
                />
              </Stack>
            </Box>

            {/* Optional filters fields */}
            {/* unmountOnExit clears the cascading selects when the filter is turned off */}
            <Collapse in={internalOptionalFilters.department} unmountOnExit>
              <Stack spacing={2}>
                <InputCatalogue
                  label={t("involved.department")}
                  catalogue={DEPARTMENT_CATALOGUE}
                  isOwn={true}
                  onChange={(value) => handleInternalFilterChange("department", value)}
                />
              </Stack>
            </Collapse>

            <Collapse in={internalOptionalFilters.position}>
              <EGBasicSelect
                label={t("involved.position")}
                value={internalFilters.position}
                onChange={(e) => handleInternalFilterChange("position", e.target.value)}
                options={positionOptions}
                placeholder={t("involved.select_position")}
              />
            </Collapse>

            <Collapse in={internalOptionalFilters.country}>
              <EGBasicSelect
                label={t("involved.country")}
                value={internalFilters.country}
                onChange={(e) => handleInternalFilterChange("country", e.target.value)}
                options={countryOptions}
                placeholder={t("involved.select_country")}
              />
            </Collapse>

            <Collapse in={internalOptionalFilters.location}>
              <EGInput
                label={t("involved.location")}
                value={internalFilters.location}
                onChange={(e) => handleInternalFilterChange("location", e.target.value)}
                placeholder={t("involved.enter_location")}
              />
            </Collapse>

            <InvestigationButton
              label={t("involved.search")}
              icon="outline_search"
              variant="primary"
              onClick={handleSearch}
              disabled={isLoading || !canSearchInternal()}
              loading={isLoading}
              fullWidth
            />
          </Stack>
        )}

        {/* External search - multiple filters */}
        {searchType === "external" && (
          <Stack spacing={2} marginBottom={2}>
            {/* Required field: Country */}
            <EGBasicSelect
              label={t("involved.country")}
              value={externalFilters.country}
              onChange={(e) => handleExternalFilterChange("country", e.target.value)}
              options={countryOptions}
              placeholder={t("involved.select_country")}
              required
            />

            {/* Required fields: firstName or lastName */}
            <Grid2 container spacing={2}>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <EGInput
                  label={t("involved.first_name")}
                  value={externalFilters.firstName}
                  onChange={(e) => handleExternalFilterChange("firstName", e.target.value)}
                  placeholder={t("involved.enter_first_name")}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <EGInput
                  label={t("involved.last_name")}
                  value={externalFilters.lastName}
                  onChange={(e) => handleExternalFilterChange("lastName", e.target.value)}
                  placeholder={t("involved.enter_last_name")}
                />
              </Grid2>
            </Grid2>

            {/* Optional filters chips */}
            <Box>
              <Typography fontSize={12} color="secondary" mb={1}>
                {t("involved.optional_filters")}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  label={t("involved.email_filter")}
                  onClick={() => toggleExternalOptionalFilter("email")}
                  color={externalOptionalFilters.email ? "primary" : "default"}
                  variant={externalOptionalFilters.email ? "filled" : "outlined"}
                  size="small"
                />
                <Chip
                  label={t("involved.business_name")}
                  onClick={() => toggleExternalOptionalFilter("businessName")}
                  color={externalOptionalFilters.businessName ? "primary" : "default"}
                  variant={externalOptionalFilters.businessName ? "filled" : "outlined"}
                  size="small"
                />
              </Stack>
            </Box>

            {/* Optional filters fields */}
            <Collapse in={externalOptionalFilters.email}>
              <EGInput
                label={t("involved.email_filter")}
                value={externalFilters.email}
                onChange={(e) => handleExternalFilterChange("email", e.target.value)}
                placeholder={t("involved.enter_email")}
              />
            </Collapse>

            <Collapse in={externalOptionalFilters.businessName}>
              <EGInput
                label={t("involved.business_name")}
                value={externalFilters.businessName}
                onChange={(e) => handleExternalFilterChange("businessName", e.target.value)}
                placeholder={t("involved.enter_business_name")}
              />
            </Collapse>

            <InvestigationButton
              label={t("involved.search")}
              icon="outline_search"
              variant="primary"
              onClick={handleSearch}
              disabled={isLoading || !canSearchExternal()}
              loading={isLoading}
              fullWidth
            />
          </Stack>
        )}

        {/* Results */}
        <Box position="relative">
          {/* Importing overlay */}
          {isImporting && (
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              bgcolor="rgba(255, 255, 255, 0.9)"
              zIndex={10}
              borderRadius="8px"
              minHeight={150}
            >
              <CircularProgress size={40} />
              <Typography
                fontSize={14}
                fontWeight={600}
                color="primary"
                mt={2}
              >
                {t("involved.importing")}
              </Typography>
            </Box>
          )}

          {isLoading && (
            <Box display="flex" justifyContent="center" padding={4}>
              <CircularProgress />
            </Box>
          )}

          {!isLoading && hasSearched && results.length === 0 && (
            <Box
              display="flex"
              justifyContent="center"
              padding={4}
              sx={{
                backgroundColor: theme.palette.blueGray50.main,
                borderRadius: "8px",
              }}
            >
              <Typography color="secondary">
                {t("involved.no_results_found")}
              </Typography>
            </Box>
          )}

          {!isLoading && hasSearched && results.length > 20 && (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              padding={4}
              sx={{
                backgroundColor: theme.palette.blueGray50.main,
                borderRadius: "8px",
              }}
            >
              <Typography color="secondary" fontWeight={600} mb={1}>
                {t("involved.too_many_results", { count: results.length })}
              </Typography>
              <Typography color="secondary" fontSize={12} textAlign="center">
                {t("involved.too_many_results_hint")}
              </Typography>
            </Box>
          )}

          {!isLoading && results.length > 0 && results.length <= 20 && (
            <Stack spacing={1.5}>
              <Typography fontSize={14} fontWeight={600} color="secondary">
                {t("involved.results")} ({results.length})
              </Typography>
              {results.map((result) => {
                // Determine if employee is active
                const isActive = result.status === "active" ||
                                 result.status === "activo" ||
                                 result.Estado === "active" ||
                                 result.Estado === "activo" ||
                                 result.isActive === true;
                const isInactive = result.status === "inactive" ||
                                   result.status === "inactivo" ||
                                   result.Estado === "inactive" ||
                                   result.Estado === "inactivo" ||
                                   result.isActive === false;

                return (
                  <InvestigationItemContainer
                    key={result.id}
                    color={isInactive ? theme.palette.blueGray.main : theme.palette.primary.main}
                    sx={{
                      padding: 2,
                      cursor: "pointer",
                      opacity: isInactive ? 0.7 : 1,
                    }}
                    onClick={() => handleSelectResult(result)}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box flex={1}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography
                            fontSize={14}
                            fontWeight={600}
                            color={isInactive ? "blueGray.main" : "primary"}
                          >
                            {result.name}
                          </Typography>
                          <Chip
                            label={isInactive ? t("involved.inactive") : t("involved.active")}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: 10,
                              fontWeight: 600,
                              backgroundColor: isInactive
                                ? `${theme.palette.blueGray.main}20`
                                : `${theme.palette.green.main}20`,
                              color: isInactive
                                ? theme.palette.blueGray.main
                                : theme.palette.green.main,
                            }}
                          />
                        </Box>
                        {searchType === "internal" ? (
                          <>
                            <Typography fontSize={12} color="secondary">
                              {result.email}
                            </Typography>
                            <Typography fontSize={11} color="blueGray600.main">
                              {[result.department, result.businessUnit, result.location].filter(Boolean).join(" • ")}
                            </Typography>
                          </>
                        ) : (
                          <>
                            <Typography fontSize={12} color="secondary">
                              {[result.country, result.email].filter(Boolean).join(" • ")}
                            </Typography>
                            {result.businessName && (
                              <Typography fontSize={11} color="blueGray600.main">
                                {result.businessName}
                              </Typography>
                            )}
                          </>
                        )}
                      </Box>
                      <InvestigationButton
                        icon="outline_user_add"
                        variant={isInactive ? "secondary" : "success"}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectResult(result);
                        }}
                      />
                    </Box>
                  </InvestigationItemContainer>
                );
              })}
            </Stack>
          )}
        </Box>
      </Box>
    </DrawerContainer>
  );
};
